import axios from 'axios';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.VITE_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

class ApiClient {
  constructor() {
    this.token = null;
    this.client = axios.create({
      baseURL: API_BASE,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response.data,
      (error) => {
        console.error('API Error:', error.response?.data || error.message);
        throw error;
      }
    );
  }

  setToken(token) {
    this.token = token;
  }

  // Auth
  async syncUser(data) {
    return this.client.post('/auth/sync', data);
  }

  async getCurrentUser() {
    return this.client.get('/auth/me');
  }

  // Countries
  async getCountries() {
    return this.client.get('/countries');
  }

  async createCountry(data) {
    return this.client.post('/countries', data);
  }

  // Vendors
  async getVendors(params = {}) {
    return this.client.get('/vendors', { params });
  }

  async getVendor(id) {
    return this.client.get(`/vendors/${id}`);
  }

  async createVendor(data) {
    return this.client.post('/vendors', data);
  }

  async updateVendor(id, data) {
    return this.client.patch(`/vendors/${id}`, data);
  }

  async deleteVendor(id) {
    return this.client.delete(`/vendors/${id}`);
  }

  async approveQualification(vendorId, requirement) {
    return this.client.post(`/vendors/${vendorId}/qualifications/${encodeURIComponent(requirement)}/approve`);
  }

  // Stores
  async getStores(params = {}) {
    return this.client.get('/stores', { params });
  }

  async getStore(id) {
    return this.client.get(`/stores/${id}`);
  }

  async createStore(data) {
    return this.client.post('/stores', data);
  }

  async importStores(stores, updateExisting = true) {
    return this.client.post('/stores/import', {
      stores,
      update_existing: updateExisting,
    });
  }

  async updateStore(id, data) {
    return this.client.patch(`/stores/${id}`, data);
  }

  async deleteStore(id) {
    return this.client.delete(`/stores/${id}`);
  }

  // Assignments
  async getAssignments(params = {}) {
    return this.client.get('/assignments', { params });
  }

  async createAssignment(data) {
    return this.client.post('/assignments', data);
  }

  async updateAssignmentStatus(id, status) {
    return this.client.patch(`/assignments/${id}`, null, { params: { status } });
  }

  async deleteAssignment(id) {
    return this.client.delete(`/assignments/${id}`);
  }

  // Collection Schedules
  async getCollectionSchedules(params = {}) {
    return this.client.get('/collection-schedules', { params });
  }

  async createCollectionSchedule(data) {
    return this.client.post('/collection-schedules', data);
  }

  // Collection Events
  async getCollectionEvents(params = {}) {
    return this.client.get('/collection-events', { params });
  }

  async createCollectionEvent(data) {
    return this.client.post('/collection-events', data);
  }

  async updateCollectionEventStatus(id, status) {
    return this.client.patch(`/collection-events/${id}/status`, null, { params: { status } });
  }

  // Offers
  async getOffers(params = {}) {
    return this.client.get('/offers', { params });
  }

  async createOffer(data) {
    return this.client.post('/offers', data);
  }

  async updateOfferStatus(id, status) {
    return this.client.patch(`/offers/${id}/status`, null, { params: { status } });
  }

  // Compliance
  async getCompliance(params = {}) {
    return this.client.get('/compliance', { params });
  }

  async createCompliance(data) {
    return this.client.post('/compliance', data);
  }

  async updateComplianceStatus(id, status) {
    return this.client.patch(`/compliance/${id}/status`, null, { params: { status } });
  }

  // Documents
  async getDocuments(params = {}) {
    return this.client.get('/documents', { params });
  }

  async createDocument(data) {
    return this.client.post('/documents', data);
  }

  async reviewDocument(id, status, rejectionReason = null) {
    return this.client.patch(`/documents/${id}/review`, null, { 
      params: { status, rejection_reason: rejectionReason } 
    });
  }

  // Exceptions
  async getExceptions(params = {}) {
    return this.client.get('/exceptions', { params });
  }

  async createException(data) {
    return this.client.post('/exceptions', data);
  }

  async resolveException(id, rootCause, correctiveAction) {
    return this.client.patch(`/exceptions/${id}/resolve`, null, { 
      params: { root_cause: rootCause, corrective_action: correctiveAction } 
    });
  }

  // Service Requests (Fexa)
  async getServiceRequests(params = {}) {
    return this.client.get('/service-requests', { params });
  }

  async createServiceRequest(data) {
    return this.client.post('/service-requests', data);
  }

  async updateServiceRequestStatus(id, status) {
    return this.client.patch(`/service-requests/${id}/status`, null, { params: { status } });
  }

  // Proposals
  async getProposals(params = {}) {
    return this.client.get('/proposals', { params });
  }

  async createProposal(data) {
    return this.client.post('/proposals', data);
  }

  // Quotes
  async getQuotes(params = {}) {
    return this.client.get('/quotes', { params });
  }

  async createQuote(data) {
    return this.client.post('/quotes', data);
  }

  async sendQuote(id) {
    return this.client.patch(`/quotes/${id}/send`);
  }

  async acceptQuote(id) {
    return this.client.patch(`/quotes/${id}/accept`);
  }

  // Invoices
  async getInvoices(params = {}) {
    return this.client.get('/invoices', { params });
  }

  async createInvoice(data) {
    return this.client.post('/invoices', data);
  }

  async sendInvoice(id) {
    return this.client.patch(`/invoices/${id}/send`);
  }

  async markInvoicePaid(id) {
    return this.client.patch(`/invoices/${id}/mark-paid`);
  }

  // Notifications
  async getNotifications() {
    return this.client.get('/notifications');
  }

  async markNotificationRead(id) {
    return this.client.patch(`/notifications/${id}/read`);
  }

  async markAllNotificationsRead() {
    return this.client.patch('/notifications/read-all');
  }

  // Dashboard
  async getDashboardStats(countryCode = null) {
    return this.client.get('/dashboard/stats', { params: { country_code: countryCode } });
  }

  async getCountryStats() {
    return this.client.get('/dashboard/country-stats');
  }

  async getRecentActivity(countryCode = null, limit = 20) {
    return this.client.get('/dashboard/recent-activity', { params: { country_code: countryCode, limit } });
  }

  async getVendorHealth(countryCode = null, limit = 10) {
    return this.client.get('/dashboard/vendor-health', { params: { country_code: countryCode, limit } });
  }

  // Analytics
  async getCollectionAnalytics(params = {}) {
    return this.client.get('/analytics/collection-stats', { params });
  }

  async getExceptionTrends(params = {}) {
    return this.client.get('/analytics/exception-trends', { params });
  }

  // Admin
  async getUsers() {
    return this.client.get('/admin/users');
  }

  async updateUserRole(userId, role, countryCodes = null) {
    return this.client.patch(`/admin/users/${userId}/role`, null, { 
      params: { role, country_codes: countryCodes } 
    });
  }

  // Health
  async healthCheck() {
    return this.client.get('/health');
  }
}

export const api = new ApiClient();
